import React from 'react';
import { BaseResourceListProps, SimpleList } from '@app/components';
import { catalogSnapshotApi } from '@app/store';
import { ICatalogSnapshot } from '@app/common';
import { theme } from '@app/theme';
import { createColumnHelper } from '@tanstack/react-table';
import { useTable } from '@app/hooks/useTable';
import { SvgTransfer } from '@app/components/icons/SvgTransfer';
import { DateTime } from 'luxon';
import { SvgEye } from '@app/components/icons/SvgEye';
import { CheckPermissions } from '@app/components/CheckPermissions';

const columnHelper = createColumnHelper<ICatalogSnapshot>();

interface CatalogSnapshotListProps extends Omit<BaseResourceListProps<ICatalogSnapshot>, "onUpdate" | "onDelete"> {
  onPublish: (catalogSnapshot: ICatalogSnapshot) => void;
  onPreview: (catalogSnapshot: ICatalogSnapshot) => void;
}

export const CatalogSnapshotList = ({ onPublish, onPreview, onRowPress, selectedRowItem }: CatalogSnapshotListProps) => {
  const columns = [
    columnHelper.accessor('name', {
      header: () => (
        <SimpleList.HeaderItem width="35%">
          <SimpleList.HeaderItemLabel>Nom</SimpleList.HeaderItemLabel>
        </SimpleList.HeaderItem>
      ),
      cell: (info) => (
        <SimpleList.ItemCol width="35%">
          <SimpleList.ItemText align="left" bold numberOfLines={2} active={info.active}>
            {info.getValue()}
          </SimpleList.ItemText>
        </SimpleList.ItemCol>
      ),
    }),
    columnHelper.accessor("connector.name", {
      header: () => (
        <SimpleList.HeaderItem width="25%">
          <SimpleList.HeaderItemLabel>Connecteur</SimpleList.HeaderItemLabel>
        </SimpleList.HeaderItem>
      ),
      cell: (info) => (
        <SimpleList.ItemCol width="25%">
          <SimpleList.ItemText align="left" bold numberOfLines={2} active={info.active}>
            {info.getValue()}
          </SimpleList.ItemText>
        </SimpleList.ItemCol>
      ),
    }),
    columnHelper.accessor("publishedAt", {
      header: () => (
        <SimpleList.HeaderItem width="25%">
          <SimpleList.HeaderItemLabel>Date de publication</SimpleList.HeaderItemLabel>
        </SimpleList.HeaderItem>
      ),
      cell: (info) => (
        <SimpleList.ItemCol width="25%">
          <SimpleList.ItemText align="left" bold numberOfLines={2} active={info.active}>
            {DateTime.fromISO(info.getValue()).toFormat('dd/MM/yyyy à HH:mm')}
          </SimpleList.ItemText>
        </SimpleList.ItemCol>
      ),
    }),
    columnHelper.display({
      id: 'actions',
      header: () => (
        <SimpleList.HeaderItem width="15%" align='center'>
          <SimpleList.HeaderItemLabel>Actions</SimpleList.HeaderItemLabel>
        </SimpleList.HeaderItem>
      ),
      cell: ({ row, active }) => (
        <SimpleList.ItemCol width="15%">
          <SimpleList.ItemActions>
            {!row.original.connector && (
              <CheckPermissions permissions={['dashboard::catalog-snapshots.publish']}>
                <SimpleList.ItemAction onPress={() => onPublish(row.original)}>
                  <SvgTransfer color={active ? '#FFF' : theme.colors.celadonblue} />
                </SimpleList.ItemAction>
              </CheckPermissions>
            )}

            <CheckPermissions permissions={['dashboard::catalog-snapshots.preview']}>
              <SimpleList.ItemAction onPress={() => onPreview(row.original)}>
                <SvgEye color={active ? '#FFF' : theme.colors.celadonblue} />
              </SimpleList.ItemAction>
            </CheckPermissions>
          </SimpleList.ItemActions>
        </SimpleList.ItemCol >
      ),
    }),
  ];

  const { table, setSearch, isFetching } = useTable({
    columns,
    useQuery: ({ pagination, search }) => catalogSnapshotApi.useReadManyCatalogSnapshotsQuery({
      pagination,
      filters: {
        name: {
          $contains: search
        }
      },
      sort: ['connector:ASC', 'publishedAt:DESC'],
      populate: [
        "connector"
      ]
    })
  });

  return <SimpleList
    table={table}
    isFetching={isFetching}
    onSearch={setSearch}
    selectedRowItem={selectedRowItem}
    onRowPress={onRowPress} />;
};
